import React, { useState } from 'react';
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { columnIsIncluded, columnSelectionError, confirmedImport, initialColumnSelection } from '../utils/importColumnReview';

function isRequiredColumn(column, selection) {
    return (column.role === 'Identifier' && column.name === selection.identifier)
        || (column.role === 'Share quantity' && column.name === selection.quantity);
}

function columnStatus(column, selection, included) {
    if (column.duplicateIdentifier) {
        return { Icon: ErrorOutlineIcon, color: 'error.main', label: 'Duplicate identifier', detail: `Ignored. ${column.reason}` };
    }
    if (!column.selectable) {
        return { Icon: ErrorOutlineIcon, color: 'error.main', label: 'Cannot include', detail: column.reason };
    }
    if (included && isRequiredColumn(column, selection)) {
        const identifier = column.role === 'Identifier';
        return {
            Icon: LockOutlinedIcon,
            color: 'text.disabled',
            label: 'Required',
            note: identifier ? 'Identifier column, not editable' : 'Share quantity column, not editable',
            detail: identifier
                ? `${column.label} is the identifier used for matching, so it is always included and can't be changed.${column.name === 'ticker' ? ' Numeric ticker symbols are supported.' : ''}`
                : `${column.label} holds the share quantities, so it is always included and can't be changed.`,
        };
    }
    if (included) {
        return {
            Icon: CheckCircleOutlineIcon,
            color: 'success.main',
            label: 'Included',
            detail: `${column.label} will be included${column.role === 'Identifier' ? ' as a display ticker' : ''}.`,
        };
    }
    if (column.role === 'Identifier' || column.role === 'Share quantity') {
        const needsChoice = column.role === 'Identifier' ? !selection.identifier : !selection.quantity;
        return {
            Icon: InfoOutlinedIcon,
            color: needsChoice ? 'warning.main' : 'text.secondary',
            label: needsChoice ? 'Choose above' : 'Not selected',
            detail: `Use the ${column.role === 'Identifier' ? 'identifier' : 'share quantity'} selector above to include this column.`,
        };
    }
    return { Icon: InfoOutlinedIcon, color: 'text.secondary', label: 'Not selected', detail: 'Optional column. Select its checkbox to include it.' };
}

// Renders a column check and returns the user's choice. A Watchlist review
// comes from the server, which also supplies its notes and limits.
export default function ImportColumnConfirmationDialog({ review, sourceLabel, onClose, onConfirm, onSwitch }) {
    const [selection, setSelection] = useState(() => initialColumnSelection(review));
    const portfolio = review.destination === 'portfolio';
    const error = columnSelectionError(review, selection);
    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Confirm import columns</DialogTitle>
            <DialogContent dividers>
                <Typography sx={{ fontWeight: 700 }}>{portfolio ? 'Actual Portfolio' : 'Watchlist'} import · {sourceLabel}</Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>Choose what to include. Required columns are included automatically. You will review the proposed changes before applying them.</Typography>
                {review.matchingNote && <Typography variant="body2" sx={{ mb: 2 }}>{review.matchingNote}</Typography>}
                {review.notices?.map((notice) => <Alert key={notice} severity="info" sx={{ mb: 2 }}>{notice}</Alert>)}
                {portfolio && !review.quantityChoices.length && <Alert severity="info" sx={{ mb: 2 }}>This file has no Quantity column. If it is a list of securities without holdings, switch to Watchlist upload.</Alert>}
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {review.identifierChoices.length > 1 && <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel id="import-identifier-label">Identifier for matching</InputLabel>
                        <Select labelId="import-identifier-label" label="Identifier for matching" value={selection.identifier} onChange={(event) => setSelection({ ...selection, identifier: event.target.value })}>
                            {review.identifierChoices.map((name) => <MenuItem key={name} value={name}>{name.toUpperCase()}</MenuItem>)}
                        </Select>
                    </FormControl>}
                    {portfolio && review.quantityChoices.length > 1 && <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="import-quantity-label">Share quantity column</InputLabel>
                        <Select labelId="import-quantity-label" label="Share quantity column" value={selection.quantity} onChange={(event) => setSelection({ ...selection, quantity: event.target.value })}>
                            {review.quantityChoices.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
                        </Select>
                    </FormControl>}
                </Box>
                {portfolio && selection.quantity && selection.quantity !== 'quantity' && <Alert severity="info" sx={{ mb: 2 }}>Continuing will use “{selection.quantity}” as Quantity (share counts).</Alert>}
                <Table size="small" aria-label="Import columns">
                    <TableHead><TableRow><TableCell>Include</TableCell><TableCell>Column</TableCell><TableCell>Use as</TableCell><TableCell>Examples / details</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                    <TableBody>{review.columns.map((column) => {
                        const included = columnIsIncluded(review, selection, column);
                        const status = columnStatus(column, selection, included);
                        const StatusIcon = status.Icon;
                        const locked = included && isRequiredColumn(column, selection);
                        const lockedSx = locked ? { color: 'text.disabled' } : undefined;
                        return <TableRow key={column.index}>
                            <TableCell><Checkbox size="small" inputProps={{ 'aria-label': `Include ${column.label}` }} checked={included} disabled={!column.selectable || ['Identifier', 'Share quantity'].includes(column.role)} onChange={(event) => setSelection({ ...selection, included: event.target.checked ? [...selection.included, column.index] : selection.included.filter((index) => index !== column.index) })} /></TableCell>
                            <TableCell sx={{ overflowWrap: 'anywhere', ...lockedSx }}>{column.label}</TableCell>
                            <TableCell sx={column.duplicateIdentifier ? { color: 'error.main' } : lockedSx}>{column.duplicateIdentifier ? 'Duplicate identifier' : column.role === 'Identifier' && column.name !== selection.identifier ? (column.name === 'ticker' && selection.identifier ? 'Display ticker' : 'Not used for matching') : column.role === 'Listing context' && review.identifierLabel ? `Listing context for ${review.identifierLabel}` : column.role}</TableCell>
                            <TableCell sx={{ maxWidth: 360, overflowWrap: 'anywhere' }}><Typography variant="body2">{column.examples.join(', ') || 'Blank'}</Typography><Typography variant="caption" color="text.secondary">{column.reason}</Typography></TableCell>
                            <TableCell sx={{ minWidth: 132 }}>
                                <Tooltip title={status.detail} describeChild>
                                    <Box component="span" tabIndex={0} aria-label={`${column.label}: ${status.label}${status.note ? `, ${status.note}` : ''}`} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: status.color }}>
                                        <StatusIcon fontSize="small" />
                                        <Box component="span">
                                            <Typography component="span" variant="body2" sx={{ display: 'block' }}>{status.label}</Typography>
                                            {status.note && <Typography component="span" variant="caption" sx={{ display: 'block', lineHeight: 1.3 }}>{status.note}</Typography>}
                                        </Box>
                                    </Box>
                                </Tooltip>
                            </TableCell>
                        </TableRow>;
                    })}</TableBody>
                </Table>
                {error && <Alert severity="info" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
                {onSwitch && <Button onClick={onSwitch}>Switch to {portfolio ? 'Watchlist' : 'Portfolio'} upload</Button>}
                <Box sx={{ flex: 1 }} />
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={Boolean(error)} onClick={() => onConfirm(confirmedImport(review, selection))}>Continue to review</Button>
            </DialogActions>
        </Dialog>
    );
}
